import { WebSocket } from "ws";
import { defineFSM } from "@dev-anywhere/shared";
import { nanoid } from "nanoid";

// 显式代理连接状态，取代 ws null 检查
type ProxyConnectionState = "online" | "offline";

// 显式客户端连接状态，跟踪注册和绑定
type ClientConnectionState = "registered" | "bound";

type RemoveOfflineProxyResult = "removed" | "not_found" | "online";

// proxy 连接 FSM: 仅 online <-> offline 双向流转。registerProxy 走 reconnect 路径直接写
// online (相当于 offline -> online), transitionProxy 走 close 路径走 online -> offline。
// 同状态停留 (online -> online / offline -> offline) 视为非法, 由 canTransition 拦下。
const proxyConnectionFSM = defineFSM<ProxyConnectionState>({
  online: ["offline"],
  offline: ["online"],
});

// 代理连接状态，跟踪 ws、会话集合、离线时间、显示名称
interface ProxyState {
  ws: WebSocket | null;
  connectionState: ProxyConnectionState;
  sessions: Set<string>;
  disconnectedAt: number | null;
  name?: string;
  osName?: string;
  version: string;
}

// 客户端绑定状态，通过 clientId 而非 WebSocket 引用标识
interface ClientBinding {
  proxyId: string;
  bindingId: string;
  ws: WebSocket | null;
  connectionState: ClientConnectionState;
}

type BoundClientSocket = WebSocket & {
  boundProxyId?: string;
  bindingId?: string;
};

interface ClientConnectionMetadata {
  clientId?: string;
  connectedAt: number;
  userAgent?: string;
  platform?: string;
  maxTouchPoints?: number;
  browserName?: string;
  osName?: string;
  deviceKind?: "desktop" | "tablet" | "phone" | "unknown";
  remoteAddress?: string;
}

// 代理注册、客户端绑定、宽限期管理
export class RelayRegistry {
  private proxyStates = new Map<string, ProxyState>();
  private clientBindings = new Map<string, ClientBinding>();
  private connectedClients = new Map<WebSocket, ClientConnectionMetadata>();

  registerProxy(
    proxyId: string,
    ws: WebSocket,
    version: string,
    name?: string,
    osName?: string,
  ): "new" | "reconnected" {
    const existing = this.proxyStates.get(proxyId);
    if (existing) {
      // 如果旧连接还活着，先终止
      if (existing.ws && existing.ws.readyState === WebSocket.OPEN) {
        existing.ws.terminate();
      }
      existing.ws = ws;
      existing.connectionState = "online";
      existing.disconnectedAt = null;
      if (name !== undefined) existing.name = name;
      existing.osName = osName;
      existing.version = version;
      return "reconnected";
    }

    this.proxyStates.set(proxyId, {
      ws,
      connectionState: "online",
      sessions: new Set(),
      disconnectedAt: null,
      name,
      osName,
      version,
    });
    return "new";
  }

  // 显式状态转换，校验 from 状态匹配后更新 connectionState
  transitionProxy(proxyId: string, from: ProxyConnectionState, to: ProxyConnectionState): void {
    if (!proxyConnectionFSM.canTransition(from, to)) {
      throw new Error(`Invalid proxy transition: ${from} -> ${to}`);
    }
    const state = this.proxyStates.get(proxyId);
    if (!state) {
      throw new Error(`Proxy not found: ${proxyId}`);
    }
    if (state.connectionState !== from) {
      throw new Error(
        `Proxy ${proxyId} state mismatch: expected ${from}, actual ${state.connectionState}`,
      );
    }
    state.connectionState = to;
    if (to === "offline") {
      state.ws = null;
      state.disconnectedAt = Date.now();
    }
  }

  getProxyConnectionState(proxyId: string): ProxyConnectionState | undefined {
    return this.proxyStates.get(proxyId)?.connectionState;
  }

  getClientConnectionState(clientId: string): ClientConnectionState | undefined {
    return this.clientBindings.get(clientId)?.connectionState;
  }

  // 彻底清理 proxy 状态和客户端绑定
  cleanupProxy(proxyId: string): void {
    const state = this.proxyStates.get(proxyId);
    if (!state) return;

    // 解绑所有绑定到该 proxy 的客户端
    for (const [clientId, binding] of this.clientBindings) {
      if (binding.proxyId === proxyId) {
        if (binding.ws) this.clearSocketBinding(binding.ws);
        this.clientBindings.delete(clientId);
      }
    }

    // ClientSocket 还单独缓存了 boundProxyId 供消息热路径路由。只删 clientBindings 会让
    // 活跃/重复 tab 残留旧 ID，并在同 ID 的 Proxy 日后重新注册时绕过重新选择直接路由。
    for (const ws of this.connectedClients.keys()) {
      const clientWs = ws as BoundClientSocket;
      if (clientWs.boundProxyId === proxyId) {
        delete clientWs.boundProxyId;
        delete clientWs.bindingId;
      }
    }

    this.proxyStates.delete(proxyId);
  }

  unregisterProxy(proxyId: string): void {
    this.cleanupProxy(proxyId);
  }

  // 检查状态与删除必须保持在同一个同步操作内，避免调用方先检查、稍后删除时误删
  // 已经重连上线的开发机。只有显式进入 offline 状态的记录才允许被用户移除。
  removeOfflineProxy(proxyId: string): RemoveOfflineProxyResult {
    const state = this.proxyStates.get(proxyId);
    if (!state) return "not_found";
    if (state.connectionState !== "offline") return "online";

    this.cleanupProxy(proxyId);
    return "removed";
  }

  getProxy(proxyId: string): WebSocket | undefined {
    const state = this.proxyStates.get(proxyId);
    return state?.ws ?? undefined;
  }

  isProxyOnline(proxyId: string): boolean {
    const state = this.proxyStates.get(proxyId);
    if (!state || state.connectionState !== "online") return false;
    // connectionState 声明在线但 ws 已失效时，记录警告并返回 false
    if (!state.ws || state.ws.readyState !== WebSocket.OPEN) return false;
    return true;
  }

  // proxy 是否存在（含宽限期中的）
  hasProxy(proxyId: string): boolean {
    return this.proxyStates.has(proxyId);
  }

  listProxies(): string[] {
    return Array.from(this.proxyStates.keys());
  }

  // 返回 proxyId、name、online 的列表，用于 proxy_list_response
  listProxiesWithName(): Array<{
    proxyId: string;
    name?: string;
    osName?: string;
    version: string;
    online: boolean;
  }> {
    return Array.from(this.proxyStates.entries()).map(([proxyId, state]) => ({
      proxyId,
      ...(state.name !== undefined ? { name: state.name } : {}),
      ...(state.osName !== undefined ? { osName: state.osName } : {}),
      version: state.version,
      online: state.connectionState === "online",
    }));
  }

  getProxyName(proxyId: string): string | undefined {
    return this.proxyStates.get(proxyId)?.name;
  }

  // 将 sessionId 关联到 proxy
  addSessionToProxy(proxyId: string, sessionId: string): void {
    const state = this.proxyStates.get(proxyId);
    if (state) {
      state.sessions.add(sessionId);
    }
  }

  setSessionsForProxy(proxyId: string, sessionIds: readonly string[]): void {
    const state = this.proxyStates.get(proxyId);
    if (state) {
      state.sessions = new Set(sessionIds);
    }
  }

  // 通过 sessionId 反查所属 proxyId
  getProxyForSession(sessionId: string): string | undefined {
    for (const [proxyId, state] of this.proxyStates) {
      if (state.sessions.has(sessionId)) {
        return proxyId;
      }
    }
    return undefined;
  }

  // 获取 proxy 关联的所有 sessionId
  getSessionsForProxy(proxyId: string): string[] {
    const state = this.proxyStates.get(proxyId);
    return state ? Array.from(state.sessions) : [];
  }

  // clientId 绑定方式
  bindClientById(clientId: string, proxyId: string, ws: WebSocket): string | undefined {
    if (!this.proxyStates.has(proxyId)) {
      return undefined;
    }
    return this.installClientBinding(clientId, proxyId, ws).bindingId;
  }

  restoreClientBinding(
    clientId: string,
    ws: WebSocket,
  ): { proxyId: string; bindingId: string } | undefined {
    const binding = this.clientBindings.get(clientId);
    if (!binding) return undefined;
    const restored = this.installClientBinding(clientId, binding.proxyId, ws);
    return { proxyId: restored.proxyId, bindingId: restored.bindingId };
  }

  // 断开客户端 WebSocket 但保留绑定关系，重连时可恢复
  unbindClientById(clientId: string): void {
    const binding = this.clientBindings.get(clientId);
    if (binding) {
      if (binding.ws) this.clearSocketBinding(binding.ws);
      binding.ws = null;
    }
  }

  // 连接关闭只能释放自己持有的绑定。旧连接的 close 可能晚于新连接注册，
  // 此时不能把已经切换到新 WebSocket 的绑定清空。
  unbindClientSocket(clientId: string, ws: WebSocket): boolean {
    const binding = this.clientBindings.get(clientId);
    if (!binding || binding.ws !== ws) return false;
    this.clearSocketBinding(ws);
    binding.ws = null;
    return true;
  }

  getClientBinding(clientId: string): ClientBinding | undefined {
    return this.clientBindings.get(clientId);
  }

  isCurrentClientBinding(
    clientId: string | undefined,
    ws: WebSocket,
    scope: { proxyId: string; bindingId: string },
  ): boolean {
    if (!clientId) return false;
    const socket = ws as BoundClientSocket;
    const binding = this.clientBindings.get(clientId);
    return (
      binding?.ws === ws &&
      binding.proxyId === scope.proxyId &&
      binding.bindingId === scope.bindingId &&
      socket.boundProxyId === scope.proxyId &&
      socket.bindingId === scope.bindingId
    );
  }

  private installClientBinding(clientId: string, proxyId: string, ws: WebSocket): ClientBinding {
    const previous = this.clientBindings.get(clientId);
    if (previous?.ws) this.clearSocketBinding(previous.ws);

    const binding: ClientBinding = {
      proxyId,
      bindingId: nanoid(),
      ws,
      connectionState: "bound",
    };
    this.clientBindings.set(clientId, binding);
    const socket = ws as BoundClientSocket;
    socket.boundProxyId = proxyId;
    socket.bindingId = binding.bindingId;
    return binding;
  }

  private clearSocketBinding(ws: WebSocket): void {
    const socket = ws as BoundClientSocket;
    delete socket.boundProxyId;
    delete socket.bindingId;
  }

  // 获取绑定到指定 proxy 的所有活跃客户端 WebSocket
  getClientsForProxy(proxyId: string): WebSocket[] {
    const clients: WebSocket[] = [];
    for (const [, binding] of this.clientBindings) {
      if (binding.proxyId === proxyId && binding.ws && binding.ws.readyState === WebSocket.OPEN) {
        clients.push(binding.ws);
      }
    }
    return clients;
  }

  countClients(): number {
    // 计数依据：ws 还存活且处于 OPEN。仅 `if (binding.ws)` 不够：close handler 漏调
    // unbindClientById 时 ws 对象残留，会让 /status 永久报告虚高 clientCount。
    let count = 0;
    for (const [, binding] of this.clientBindings) {
      if (binding.ws && binding.ws.readyState === WebSocket.OPEN) count++;
    }
    return count;
  }

  addClientWs(
    ws: WebSocket,
    metadata: Partial<Omit<ClientConnectionMetadata, "connectedAt">> & {
      connectedAt?: number;
    } = {},
  ): void {
    this.connectedClients.set(ws, {
      connectedAt: metadata.connectedAt ?? Date.now(),
      ...(metadata.clientId !== undefined ? { clientId: metadata.clientId } : {}),
      ...(metadata.userAgent !== undefined ? { userAgent: metadata.userAgent } : {}),
      ...(metadata.platform !== undefined ? { platform: metadata.platform } : {}),
      ...(metadata.maxTouchPoints !== undefined ? { maxTouchPoints: metadata.maxTouchPoints } : {}),
      ...(metadata.browserName !== undefined ? { browserName: metadata.browserName } : {}),
      ...(metadata.osName !== undefined ? { osName: metadata.osName } : {}),
      ...(metadata.deviceKind !== undefined ? { deviceKind: metadata.deviceKind } : {}),
      ...(metadata.remoteAddress !== undefined ? { remoteAddress: metadata.remoteAddress } : {}),
    });
  }

  updateConnectedClientId(ws: WebSocket, clientId: string): void {
    const metadata = this.connectedClients.get(ws);
    if (metadata) {
      metadata.clientId = clientId;
      return;
    }
    this.addClientWs(ws, { clientId });
  }

  updateConnectedClientMetadata(
    ws: WebSocket,
    metadata: Partial<Omit<ClientConnectionMetadata, "connectedAt">>,
  ): void {
    const current = this.connectedClients.get(ws);
    if (!current) {
      this.addClientWs(ws, metadata);
      return;
    }
    if (metadata.clientId !== undefined) current.clientId = metadata.clientId;
    if (metadata.userAgent !== undefined) current.userAgent = metadata.userAgent;
    if (metadata.platform !== undefined) current.platform = metadata.platform;
    if (metadata.maxTouchPoints !== undefined) current.maxTouchPoints = metadata.maxTouchPoints;
    if (metadata.browserName !== undefined) current.browserName = metadata.browserName;
    if (metadata.osName !== undefined) current.osName = metadata.osName;
    if (metadata.deviceKind !== undefined) current.deviceKind = metadata.deviceKind;
    if (metadata.remoteAddress !== undefined) current.remoteAddress = metadata.remoteAddress;
  }

  removeClientWs(ws: WebSocket): void {
    this.connectedClients.delete(ws);
  }

  getAllClientWs(): WebSocket[] {
    const clients: WebSocket[] = [];
    for (const [ws, metadata] of this.connectedClients) {
      // Raw /client sockets are tracked immediately so they can be inspected and cleaned up, but
      // they must not receive business broadcasts before client_register has completed. The
      // registration response is the admission boundary observed by Web clients.
      if (metadata.clientId && ws.readyState === WebSocket.OPEN) {
        clients.push(ws);
      }
    }
    return clients;
  }

  getConnectedClientSockets(clientId: string): WebSocket[] {
    const sockets: WebSocket[] = [];
    for (const [ws, metadata] of this.connectedClients) {
      if (metadata.clientId === clientId && ws.readyState === WebSocket.OPEN) {
        sockets.push(ws);
      }
    }
    return sockets;
  }

  // 获取单个 proxy 的详细状态信息
  getProxyDetail(proxyId: string):
    | {
        proxyId: string;
        name?: string;
        online: boolean;
        connectionState: ProxyConnectionState;
        sessions: string[];
        disconnectedAt: number | null;
      }
    | undefined {
    const state = this.proxyStates.get(proxyId);
    if (!state) return undefined;
    return {
      proxyId,
      ...(state.name !== undefined ? { name: state.name } : {}),
      online: state.connectionState === "online",
      connectionState: state.connectionState,
      sessions: Array.from(state.sessions),
      disconnectedAt: state.disconnectedAt,
    };
  }

  // 获取所有客户端绑定的详细信息
  getClientDetails(): Array<{
    clientId: string;
    proxyId: string;
    online: boolean;
    connectionState: ClientConnectionState;
  }> {
    const details: Array<{
      clientId: string;
      proxyId: string;
      online: boolean;
      connectionState: ClientConnectionState;
    }> = [];
    for (const [clientId, binding] of this.clientBindings) {
      details.push({
        clientId,
        proxyId: binding.proxyId,
        online:
          binding.ws !== null &&
          binding.ws !== undefined &&
          binding.ws.readyState === WebSocket.OPEN,
        connectionState: binding.connectionState,
      });
    }
    return details;
  }

  getConnectedClientDetails(currentClientId?: string): Array<{
    clientId: string;
    proxyId?: string;
    connectedAt: number;
    current?: boolean;
    userAgent?: string;
    platform?: string;
    maxTouchPoints?: number;
    browserName: string;
    osName: string;
    deviceKind: "desktop" | "tablet" | "phone" | "unknown";
    remoteAddress?: string;
  }> {
    const details: Array<{
      clientId: string;
      proxyId?: string;
      connectedAt: number;
      current?: boolean;
      userAgent?: string;
      platform?: string;
      maxTouchPoints?: number;
      browserName: string;
      osName: string;
      deviceKind: "desktop" | "tablet" | "phone" | "unknown";
      remoteAddress?: string;
    }> = [];
    for (const [ws, metadata] of this.connectedClients) {
      if (ws.readyState !== WebSocket.OPEN || !metadata.clientId) continue;
      if (!metadata.browserName || !metadata.osName || !metadata.deviceKind) continue;
      const binding = this.clientBindings.get(metadata.clientId);
      details.push({
        clientId: metadata.clientId,
        ...(binding?.proxyId !== undefined ? { proxyId: binding.proxyId } : {}),
        connectedAt: metadata.connectedAt,
        ...(metadata.clientId === currentClientId ? { current: true } : {}),
        ...(metadata.userAgent !== undefined ? { userAgent: metadata.userAgent } : {}),
        ...(metadata.platform !== undefined ? { platform: metadata.platform } : {}),
        ...(metadata.maxTouchPoints !== undefined
          ? { maxTouchPoints: metadata.maxTouchPoints }
          : {}),
        browserName: metadata.browserName,
        osName: metadata.osName,
        deviceKind: metadata.deviceKind,
        ...(metadata.remoteAddress !== undefined ? { remoteAddress: metadata.remoteAddress } : {}),
      });
    }
    return details;
  }
}
